import _ from 'underscore';
import { hasScheme } from '@bingads-webui/url-util';

export const adFieldLengthLimitMap = {
  TrackingUrlTemplate: 2048,
  FinalUrlSuffix: 1024,
  UrlCustomParametersKey: 16,
  UrlCustomParametersValue: 2048,
};

export function checkAdFieldLengthLimit(value, key) {
  return _.size(value) <= adFieldLengthLimitMap[key];
}

export function isUrlCustomParametersEmpty(customParameters) {
  return !_.some(customParameters, ({ Key, Value }) => !_.isEmpty(Key) || !_.isEmpty(Value));
}

export function validateFinalUrlSuffix(finalUrlSuffix, i18n) {
  const errors = [];
  const invalidFirstChars = ['#', '?', '&'];
  const firstChar = _.first(finalUrlSuffix);

  if (_.size(finalUrlSuffix) > adFieldLengthLimitMap.FinalUrlSuffix) {
    errors.push({
      Property: 'FinalUrlSuffix',
      Message: i18n.getString(_TL_('Field should not exceed {{maxlength}} characters'), { maxlength: adFieldLengthLimitMap.FinalUrlSuffix }),
    });
  }

  if (_.contains(invalidFirstChars, firstChar) || /\s|\r|\n/g.test(finalUrlSuffix)) {
    errors.push({
      Property: 'FinalUrlSuffix',
      Message: i18n.getString(_TL_('Value contains invalid characters.')),
    });
  }

  // Parameters reserved for tracking template can not be used here.
  if (!_.isEmpty(finalUrlSuffix) && _.find(
    ['{lpurl}',
      '{lpurl+2}',
      '{lpurl+3}',
      '{unescapedlpurl}',
      '{ignore}',
      '{escapedlpurl}',
      '{escapedlpurl+2}',
      '{escapedlpurl+3}',
      '{ignore}',
    ],
    tag => finalUrlSuffix.indexOf(tag) === 0
  )) {
    errors.push({
      Property: 'FinalUrlSuffix',
      Message: i18n.getString(_TL_('Value contains invalid tag.')),
    });
  }

  return errors;
}

export function validateTrackingUrlTemplate(trackingUrlTemplate, i18n) {
  const errors = [];
  if (_.size(trackingUrlTemplate) > adFieldLengthLimitMap.TrackingUrlTemplate) {
    errors.push({
      Property: 'TrackingUrlTemplate',
      Message: i18n.getString(_TL_('Field should not exceed {{maxlength}} characters'), { maxlength: adFieldLengthLimitMap.TrackingUrlTemplate }),
    });
  }

  if (!_.isEmpty(trackingUrlTemplate) &&
    !hasScheme(trackingUrlTemplate) &&
    !_.find(
      ['{lpurl}', '{unescapedlpurl}'],
      tag => trackingUrlTemplate.indexOf(tag) === 0
    )) {
    errors.push({
      Property: 'TrackingUrlTemplate',
      Message: i18n.getString(_TL_('The URL entered is invalid. A valid URL must start with http:// or https:// or {lpurl} or {unescapedlpurl}.')),
    });
  }

  return errors;
}

export function validateTrackingUrlTemplateStringOnly(trackingUrlTemplate, i18n, isTrackingUrlTemplateRequired = false) {
  const errors = [];
  if (_.size(trackingUrlTemplate) > adFieldLengthLimitMap.TrackingUrlTemplate) {
    errors.push(i18n.getString(_TL_('Field should not exceed {{maxlength}} characters'), { maxlength: adFieldLengthLimitMap.TrackingUrlTemplate }));
  }

  if (!_.isEmpty(trackingUrlTemplate) &&
    !hasScheme(trackingUrlTemplate) &&
    !_.find(
      ['{lpurl}', '{unescapedlpurl}'],
      tag => trackingUrlTemplate.indexOf(tag) === 0
    )) {
    errors.push(i18n.getString(_TL_('The URL entered is invalid. A valid URL must start with http:// or https:// or {lpurl} or {unescapedlpurl}.')));
  }

  if (isTrackingUrlTemplateRequired && _.isEmpty(trackingUrlTemplate)) {
    errors.push(i18n.getString(_TL_('Please add a tracking template.')));
  }

  return errors;
}

const invalidChars = ['#', '/', ' '];

export function validateUrlCustomParameters(urlCustomParameters, i18n) {
  const errors = [];

  _.each(urlCustomParameters, ({ Key, Value }, index) => {
    if (_.size(Key) > adFieldLengthLimitMap.UrlCustomParametersKey) {
      errors.push({
        Property: 'UrlCustomParameters',
        Message: i18n.getString(_TL_('Name should not exceed {{maxlength}} characters'), { maxlength: adFieldLengthLimitMap.UrlCustomParametersKey }),
        Index: index,
      });
    }
    if (_.size(Value) > adFieldLengthLimitMap.UrlCustomParametersValue) {
      errors.push({
        Property: 'UrlCustomParameters',
        Message: i18n.getString(_TL_('Value should not exceed {{maxlength}} characters'), { maxlength: adFieldLengthLimitMap.UrlCustomParametersValue }),
        Index: index,
      });
    }

    if (Value && !Key) {
      errors.push({
        Property: 'UrlCustomParameters',
        Message: i18n.getString(_TL_('Custom parameter requires a name.')),
        Index: index,
      });
    }
    if (_.some(invalidChars, c => _.contains(Key, c))) {
      errors.push({
        Property: 'UrlCustomParameters',
        Message: i18n.getString(_TL_('The name of a custom parameter can only contain alphanumeric characters (e.g., 0-9 a-z A-Z).')),
        Index: index,
      });
    }
    if (/\s/.test(Value)) { // Validate value against white spaces
      errors.push({
        Property: 'UrlCustomParameters',
        Message: i18n.getString(_TL_('Value contains invalid characters.')),
        Index: index,
      });
    }
  });

  const keys = _.compact(_.map(urlCustomParameters, ({ Key }) => Key.toLowerCase()));
  if (_.unique(keys).length !== keys.length) {
    errors.push({
      Property: 'UrlCustomParameters',
      Message: i18n.getString(_TL_('Custom parameter names must be unique.')),
      Index: -1,
    });
  }

  return errors;
}
